import trains from './trains.json';
import { timeToSeconds, timeDiff } from './utils.js';
import intersection from 'lodash/fp/intersection';
import zip from 'lodash/fp/zip';
import sum from 'lodash/fp/sum';
import keys from 'lodash/fp/keys';

const routeCache = {};
const trainCache = {};

const restrictionsCompatible = route => {
  if(route.legs.length !== 2) {
    return true;
  }
  if(route.legs[0].train.restrictions.length === 1
      && route.legs[1].train.restrictions.length === 1) {
    const left = route.legs[0].train.restrictions[0];
    const right = route.legs[1].train.restrictions[0];


    if(left.from === right.from && left.to === right.to) {
      const leftWeekbits = left.daysOfWeek & 0b1111111;
      const rightWeekbits = right.daysOfWeek & 0b1111111;
      return leftWeekbits & rightWeekbits !== 0;
    } else {
      // TODO: make it more robust
      return true;
    }
  }

  // TODO: make it more robust
  return true;
};

export function findTrain(train) {
  if(typeof trainCache[train] !== 'undefined') {
    return trainCache[train];
  }

  return trains.filter(currentTrain => `${currentTrain.type} ${currentTrain.number}` === train).map(currentTrain => {
    return {
      from: currentTrain.segments[0].from,
      to: currentTrain.segments[currentTrain.segments.length - 1].to,
      leavesAt: currentTrain.segments[0].leavesAt,
      arrivesAt: currentTrain.segments[currentTrain.segments.length - 1].arrivesAt,
      legs: [{
        train: currentTrain,
        from: currentTrain.segments[0].from,
        to: currentTrain.segments[currentTrain.segments.length - 1].to,
        leavesAt: currentTrain.segments[0].leavesAt,
        arrivesAt: currentTrain.segments[currentTrain.segments.length - 1].arrivesAt,
        segments: currentTrain.segments
      }]
    };
  });
}

export function findRoute(from, to) {
  if(typeof routeCache[from] !== 'undefined' && typeof routeCache[from][to] !== 'undefined') {
    return routeCache[from][to];
  }

  let routes = [];
  const intermediaryStationsFrom = {};
  const intermediaryStationsTo = {};

  trains.map(train => {
    let hasSourceAt = -1;
    let hasDestinationAt = -1;
    train.segments.map((segment, idx) => {
      if(segment.from === from) {
        hasSourceAt = idx;
      } else if(segment.to === to) {
        hasDestinationAt = idx;
      }
    });

    if(hasSourceAt !== -1 && hasDestinationAt === -1) {
      train.segments.slice(hasSourceAt).map((segment, idx) => {
        if(typeof intermediaryStationsFrom[segment.to] === 'undefined') {
          intermediaryStationsFrom[segment.to] = [];
        }
        intermediaryStationsFrom[segment.to].push({
          train,
          from,
          to: segment.to,
          leavesAt: train.segments[hasSourceAt].leavesAt,
          arrivesAt: train.segments[hasSourceAt + idx].arrivesAt,
          segments: train.segments.slice(hasSourceAt, hasSourceAt + 1 + idx)
        });
      });
    } else if(hasDestinationAt !== -1 && hasSourceAt === -1) {
      train.segments.slice(0, hasDestinationAt).map((segment, idx) => {
        if(typeof intermediaryStationsTo[segment.from] === 'undefined') {
          intermediaryStationsTo[segment.from] = [];
        }
        intermediaryStationsTo[segment.from].push({
          train,
          from: segment.from,
          to,
          leavesAt: train.segments[idx].leavesAt,
          arrivesAt: train.segments[hasDestinationAt].arrivesAt,
          segments: train.segments.slice(idx, hasDestinationAt + 1)
        });
      });
    }

    if(hasSourceAt !== -1 && hasDestinationAt !== -1 && hasSourceAt < hasDestinationAt) {
      routes.push({
        from,
        to,
        leavesAt: train.segments[hasSourceAt].leavesAt,
        arrivesAt: train.segments[hasDestinationAt].arrivesAt,
        legs: [{
          train,
          from,
          to,
          leavesAt: train.segments[hasSourceAt].leavesAt,
          arrivesAt: train.segments[hasDestinationAt].arrivesAt,
          segments: train.segments.slice(hasSourceAt, hasDestinationAt + 1)
        }]
      });
    }
  });

  const last = arr => arr[arr.length - 1];
  intersection(
    keys(intermediaryStationsFrom),
    keys(intermediaryStationsTo)
  ).map(intermediaryStation => {
    intermediaryStationsFrom[intermediaryStation].map(firstLeg => {
      intermediaryStationsTo[intermediaryStation].map(secondLeg => {
        const timeWaiting = timeDiff(
          last(firstLeg.segments).arrivesAt,
          secondLeg.segments[0].leavesAt
        );
        if(timeWaiting > 5 * 3600) {
          // don't wait for more than 5h
          return
        }

        routes.push({
          from,
          to,
          leavesAt: firstLeg.leavesAt,
          arrivesAt: secondLeg.arrivesAt,
          legs: [firstLeg, secondLeg]
        });
      });
    });
  });

  // remove routes that are significantly slower than the fastest
  let fastestTime = Infinity;
  const routeTime = route => {
    const actualTime = sum(
      route.legs.map(leg => timeDiff(leg.leavesAt, leg.arrivesAt))
    );
    const waitingTime = sum(
      zip(
        route.legs.slice(0, -1),
        route.legs.slice(1)
      ).map(([leg, nextLeg]) => timeDiff(leg.arrivesAt, nextLeg.leavesAt))
    );
    return actualTime + waitingTime;
  }
  routes.map(route => {
    fastestTime = Math.min(fastestTime, routeTime(route));
  });

  routes = routes.filter(route => routeTime(route) < fastestTime * 1.5);
  routes = routes.filter(restrictionsCompatible);

  routes.sort((left, right) => {
    const leaveDiff = timeToSeconds(left.leavesAt) - timeToSeconds(right.leavesAt);
    if(!leaveDiff) {
      return timeToSeconds(left.arrivesAt) - timeToSeconds(right.arrivesAt);
    }
    return leaveDiff;
  });

  if(typeof routeCache[from] === 'undefined') {
    routeCache[from] = {};
  }

  routeCache[from][to] = routes;
  return routes;
}

export function findRouteFrom(source) {
  const routes = [];
  trains.map(train => {
    train.segments.map((segment, idx) => {
      if(segment.from === source) {
        routes.push({
          from: source,
          to: train.segments[train.segments.length - 1].to,
          leavesAt: segment.leavesAt,
          arrivesAt: train.segments[train.segments.length - 1].arrivesAt,
          legs: [{
            from: source,
            to: train.segments[train.segments.length - 1].to,
            leavesAt: segment.leavesAt,
            arrivesAt: train.segments[train.segments.length - 1].arrivesAt,
            train,
            segments: train.segments.slice(idx)
          }]
        });
      }
    });
  });

  return routes;
}

export function findRouteTo(destination) {
  const routes = [];
  trains.map(train => {
    train.segments.map((segment, idx) => {
      if(segment.to === destination) {
        routes.push({
          from: train.segments[0].from,
          to: destination,
          leavesAt: train.segments[0].leavesAt,
          arrivesAt: segment.arrivesAt,
          legs: [{
            from: train.segments[0].from,
            to: destination,
            leavesAt: train.segments[0].leavesAt,
            arrivesAt: segment.arrivesAt,
            train,
            segments: train.segments.slice(0, idx + 1)
          }]
        });
      }
    });
  });

  return routes;
}

export function getStations() {
  const stations = {};
  trains.map(train => train.segments.map(segment => {
    stations[segment.from] = (stations[segment.from] || 0) + 1;
    stations[segment.to] = (stations[segment.to] || 0) + 1;
  }));

  const stationNames = Object.keys(stations);
  stationNames.sort((a, b) => stations[b] - stations[a]);

  return stationNames;
}

export function getTrains() {
  return trains.map(train => `${train.type} ${train.number}`);
}



/** WEBPACK FOOTER **
 ** ./src/finder.js
 **/